import React, { useCallback } from "react";
import { observer } from "mobx-react-lite";
import { Dialog } from "@/components/toolkit/Dialog";
import { Clickable } from "@/components/toolkit/Clickable";
import { TextInput } from "@/components/toolkit/TextInput";
import { SelectInput } from "@/components/toolkit/SelectInput";
import { t } from "@/i18n";
import type { ProjectStore, VaultStore } from "@/app_store/agent";
import "./s.configure_vault_modal.css";

export const ConfigureVaultModal = observer(function ConfigureVaultModal(props: {
    project: ProjectStore;
    activeId: string | null;
    onActiveIdChange: (id: string | null) => void;
    onClose: () => void;
}) {
    const { project, activeId, onActiveIdChange, onClose } = props;
    const vaults = Object.values(project.vaults);
    const activeVault: VaultStore | null = activeId ? (project.vaults[activeId] ?? null) : null;

    const addVault = useCallback(async () => {
        const entry = await project.add_vault("", "local_folder");
        if (entry?.id) onActiveIdChange(entry.id);
    }, [project, onActiveIdChange]);

    const removeVault = useCallback(async (id: string) => {
        const remaining = vaults.filter(v => v.id !== id);
        await project.remove_vault(id);
        onActiveIdChange(remaining[0]?.id ?? null);
    }, [project, vaults, onActiveIdChange]);

    const updateName = useCallback(async (vault: VaultStore, name: string) => {
        await vault.update_settings(name, vault.engine);
    }, []);

    const updateEngine = useCallback(async (vault: VaultStore, engine: string) => {
        await vault.update_settings(vault.name, engine);
    }, []);

    return (
        <Dialog title={t("vault_configure_modal_title")} onClose={onClose}>
            <div className="configure_vault_modal__body">
                {vaults.length === 0 ? (
                    <div className="configure_vault_modal__empty">
                        <Clickable onClick={addVault}>
                            <button className="configure_vault_modal__empty_btn">
                                + {t("vault_add_new")}
                            </button>
                        </Clickable>
                    </div>
                ) : (
                    <>
                        <div className="configure_vault_modal__tabs_bar">
                            {vaults.map(vault => (
                                <div
                                    key={vault.id}
                                    className={"configure_vault_modal__tab" + (vault.id === activeId ? " active" : "")}
                                    onClick={() => onActiveIdChange(vault.id)}
                                >
                                    <span className="configure_vault_modal__tab_label">
                                        {vault.name.trim() || t("vault_unnamed")}
                                    </span>
                                    <span
                                        className="configure_vault_modal__tab_close"
                                        onClick={e => { e.stopPropagation(); removeVault(vault.id); }}
                                    >×</span>
                                </div>
                            ))}
                            <button
                                className="configure_vault_modal__new_tab_btn"
                                onClick={addVault}
                                title={t("vault_add_new")}
                            >+</button>
                        </div>

                        {activeVault && (
                            <div className="configure_vault_modal__form">
                                <div className="configure_vault_modal__field">
                                    <label className="configure_vault_modal__field_label">
                                        {t("vault_name_label")}
                                    </label>
                                    <TextInput
                                        value={activeVault.name}
                                        placeholder={t("vault_unnamed")}
                                        autoFocus
                                        onChange={v => updateName(activeVault, v)}
                                    />
                                </div>
                                <div className="configure_vault_modal__field">
                                    <label className="configure_vault_modal__field_label">
                                        {t("vault_engine_label")}
                                    </label>
                                    <SelectInput
                                        value={activeVault.engine}
                                        options={[{ value: "local_folder", title: t("vault_engine_local_folder") }]}
                                        onChange={v => updateEngine(activeVault, v)}
                                    />
                                </div>
                            </div>
                        )}
                    </>
                )}
            </div>

            <div className="configure_vault_modal__actions">
                <Clickable onClick={onClose}>
                    <button className="configure_vault_modal__btn configure_vault_modal__btn--cancel">
                        {t("settings_cancel")}
                    </button>
                </Clickable>
                <Clickable onClick={onClose}>
                    <button className="configure_vault_modal__btn configure_vault_modal__btn--save">
                        {t("settings_save")}
                    </button>
                </Clickable>
            </div>
        </Dialog>
    );
});
